/**
 * insightsSelector.ts - Integrated.Social
 *
 * Deterministic scored selector for the homepage "Three AI Growth Insights" section.
 * Spec §10: selection must be deterministic, server-rendered, and recalculated on a
 * controlled schedule. This implementation uses available BlogPost fields to approximate
 * the full InsightMetadata scoring model.
 *
 * Scoring model (max 100):
 *   freshness          max 25  - based on date / dateModified
 *   businessAlignment  max 20  - based on tag alignment to core services
 *   editorialImpact    max 20  - based on aiSummary presence + faqSchema count
 *   strategicPriority  max 15  - based on tag strategic tier
 *   searchOpportunity  max 10  - based on slug keyword signals
 *   internalLinkNeed   max  5  - based on relatedSlugs count
 *   diversity          max  5  - applied at selection time
 *   penalties          variable
 */

import type { BlogPost } from "@/data/blogPosts";

// Strategic service tag alignment (business alignment score)
const HIGH_ALIGNMENT_TAGS = [
  "Agentic AI",
  "AEO & AI Search",
  "PPC & Performance Max",
  "AI Marketing Strategy",
];
const MEDIUM_ALIGNMENT_TAGS = [
  "AI News & Trends",
  "Digital Marketing Tips",
];

// Strategic priority tier by tag
const STRATEGIC_TIER_1 = ["Agentic AI", "AEO & AI Search"];
const STRATEGIC_TIER_2 = ["PPC & Performance Max", "AI Marketing Strategy"];

// Slugs that are pinned (manually curated - max 1 active pin at a time)
// Format: { slug, reason, owner, pinnedUntil: "YYYY-MM-DD" }
const MANUAL_PINS: { slug: string; reason: string; owner: string; pinnedUntil: string }[] = [
  {
    slug: "ai-visibility-benchmark-2026-citation-rates-share-of-voice",
    reason: "Phase 4 AI Visibility Benchmark feature placement (21 days)",
    owner: "Modi Elnadi",
    pinnedUntil: "2026-10-15",
  },
];

export interface ScoredInsight {
  post: BlogPost;
  score: number;
  breakdown: {
    freshness: number;
    businessAlignment: number;
    editorialImpact: number;
    strategicPriority: number;
    searchOpportunity: number;
    internalLinkNeed: number;
    diversity: number;
    penalties: number;
  };
  pinned: boolean;
}

/**
 * Homepage filters intentionally use the established editorial taxonomy rather
 * than every individual post tag. This keeps the control useful on small
 * screens and ensures a chosen filter still represents a meaningful topic.
 */
export const HOMEPAGE_INSIGHT_FILTER_TAGS = [
  "AEO & AI Search",
  "AI Marketing Strategy",
  "Agentic AI",
  "AI Governance & Safety",
  "PPC & Performance Max",
  "AI News & Trends",
  "Digital Marketing Tips",
] as const;

export type HomepageInsightFilter = "All" | (typeof HOMEPAGE_INSIGHT_FILTER_TAGS)[number];

/** Return only editorial categories represented by currently published posts. */
export function getHomepageInsightFilters(posts: BlogPost[]): HomepageInsightFilter[] {
  const presentTags = new Set(posts.flatMap((post) => post.tags || []));
  return [
    "All",
    ...HOMEPAGE_INSIGHT_FILTER_TAGS.filter((tag) => presentTags.has(tag)),
  ];
}

function getFreshnessScore(post: BlogPost, referenceDate: Date): number {
  const dateStr = post.dateModified || post.date;
  const postDate = new Date(dateStr);
  const diffDays = Math.floor(
    (referenceDate.getTime() - postDate.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (diffDays <= 3) return 25;
  if (diffDays <= 7) return 22;
  if (diffDays <= 14) return 18;
  if (diffDays <= 30) return 12;
  if (diffDays <= 60) return 6;
  return 0;
}

function getBusinessAlignmentScore(post: BlogPost): number {
  const tags = post.tags || [];
  const highCount = tags.filter((t) => HIGH_ALIGNMENT_TAGS.includes(t)).length;
  const medCount = tags.filter((t) => MEDIUM_ALIGNMENT_TAGS.includes(t)).length;
  if (highCount >= 2) return 20;
  if (highCount === 1 && medCount >= 1) return 16;
  if (highCount === 1) return 13;
  if (medCount >= 2) return 8;
  if (medCount === 1) return 5;
  return 2;
}

function getEditorialImpactScore(post: BlogPost): number {
  let score = 0;
  // aiSummary presence = strong editorial signal
  if (post.aiSummary && post.aiSummary.length >= 4) score += 12;
  else if (post.aiSummary && post.aiSummary.length >= 2) score += 7;
  // FAQ depth
  const faqCount = post.faqSchema?.length || 0;
  if (faqCount >= 6) score += 8;
  else if (faqCount >= 4) score += 5;
  else if (faqCount >= 2) score += 2;
  return Math.min(score, 20);
}

function getStrategicPriorityScore(post: BlogPost): number {
  const tags = post.tags || [];
  const tier1 = tags.some((t) => STRATEGIC_TIER_1.includes(t));
  const tier2 = tags.some((t) => STRATEGIC_TIER_2.includes(t));
  if (tier1 && tier2) return 15;
  if (tier1) return 12;
  if (tier2) return 8;
  return 3;
}

function getSearchOpportunityScore(post: BlogPost): number {
  const slug = post.slug || "";
  // High-value keyword signals in slug
  const highValueTerms = [
    "aeo", "geo", "llmo", "ai-search", "agentic", "chatgpt", "gemini",
    "perplexity", "google-ai", "ai-mode", "ppc", "lead-gen", "pipeline",
    "b2b", "marketing-strategy", "ai-visibility",
  ];
  const matchCount = highValueTerms.filter((t) => slug.includes(t)).length;
  if (matchCount >= 3) return 10;
  if (matchCount === 2) return 7;
  if (matchCount === 1) return 4;
  return 1;
}

function getInternalLinkNeedScore(post: BlogPost): number {
  const relatedCount = post.relatedSlugs?.length || 0;
  // Posts with fewer related slugs benefit more from homepage exposure
  if (relatedCount === 0) return 5;
  if (relatedCount <= 2) return 3;
  return 1;
}

function getTopicCluster(post: BlogPost): string {
  const tags = post.tags || [];
  if (tags.includes("Agentic AI")) return "agentic-ai";
  if (tags.includes("AEO & AI Search")) return "aeo-search";
  if (tags.includes("PPC & Performance Max")) return "ppc";
  if (tags.includes("AI Marketing Strategy")) return "ai-strategy";
  if (tags.includes("AI Governance & Safety")) return "ai-governance";
  if (tags.includes("AI News & Trends")) return "ai-news";
  return "general";
}

/**
 * Select exactly 3 eligible articles using the deterministic scoring model.
 * referenceDate defaults to today (UTC) - pass a fixed date for SSR consistency.
 */
export function selectInsights(
  allPosts: BlogPost[],
  referenceDate: Date = new Date()
): ScoredInsight[] {
  const today = new Date(referenceDate);
  today.setHours(0, 0, 0, 0);

  // Hard eligibility gate - exclude future posts and posts without required fields
  const eligible = allPosts.filter((post) => {
    if (!post.slug || !post.title || !post.heroImage || !post.date) return false;
    const postDate = new Date(post.date);
    if (postDate > today) return false; // no future posts
    if (!post.excerpt || post.excerpt.length < 20) return false;
    return true;
  });

  // Check for active manual pins
  const activePins = MANUAL_PINS.filter((pin) => {
    const expiry = new Date(pin.pinnedUntil);
    return expiry >= today;
  });
  const pinnedSlug = activePins.length > 0 ? activePins[0].slug : null;

  // Score all eligible posts
  const scored: ScoredInsight[] = eligible.map((post) => {
    const freshness = getFreshnessScore(post, today);
    const businessAlignment = getBusinessAlignmentScore(post);
    const editorialImpact = getEditorialImpactScore(post);
    const strategicPriority = getStrategicPriorityScore(post);
    const searchOpportunity = getSearchOpportunityScore(post);
    const internalLinkNeed = getInternalLinkNeedScore(post);

    // Penalties
    let penalties = 0;
    if (!post.heroImage || post.heroImage.includes("placeholder")) penalties += 3;

    const rawTotal =
      freshness +
      businessAlignment +
      editorialImpact +
      strategicPriority +
      searchOpportunity +
      internalLinkNeed -
      penalties;

    return {
      post,
      score: Math.max(0, Math.min(100, rawTotal)),
      breakdown: {
        freshness,
        businessAlignment,
        editorialImpact,
        strategicPriority,
        searchOpportunity,
        internalLinkNeed,
        diversity: 0, // applied at selection time
        penalties,
      },
      pinned: post.slug === pinnedSlug,
    };
  });

  // Sort by score descending, then by date descending for tie-breaking
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return new Date(b.post.date).getTime() - new Date(a.post.date).getTime();
  });

  // Greedy selection with diversity enforcement
  const selected: ScoredInsight[] = [];
  const usedClusters = new Set<string>();

  // Always include pinned post first (if any)
  if (pinnedSlug) {
    const pinned = scored.find((s) => s.post.slug === pinnedSlug);
    if (pinned) {
      pinned.breakdown.diversity = 5;
      pinned.score = Math.min(100, pinned.score + 5);
      selected.push(pinned);
      usedClusters.add(getTopicCluster(pinned.post));
    }
  }

  // Fill remaining slots
  for (const candidate of scored) {
    if (selected.length >= 3) break;
    if (selected.some((s) => s.post.slug === candidate.post.slug)) continue;

    const cluster = getTopicCluster(candidate.post);
    const clusterCount = Array.from(usedClusters).filter((c) => c === cluster).length;

    // Penalty: same cluster as already selected
    if (clusterCount >= 2) continue; // exclude if cluster already has 2 articles
    if (clusterCount === 1) {
      candidate.score = Math.max(0, candidate.score - 10);
      candidate.breakdown.penalties += 10;
    } else {
      // Diversity bonus for new cluster
      candidate.breakdown.diversity = 5;
      candidate.score = Math.min(100, candidate.score + 5);
    }

    selected.push(candidate);
    usedClusters.add(cluster);
  }

  return selected.slice(0, 3);
}
